/**
 * @file        src/pages/payment/TaxManagement.jsx
 * @description Tax / invoice company settings (single-row upsert).
 * @author      Kinetika Labs
 * @created     2026-07-03
 * @changelog
 *   - 2026-07-03 Claude — Initial (Payment Management).
 *   - 2026-09-15 Claude — Added a warning note that this screen's values are not
 *                          printed anywhere. `tax_company_settings` is a
 *                          write-orphan: the documents read `site_settings` via
 *                          useCompany() (migration 051), and hsn_code /
 *                          gst_state_code / registration_no appear in no other
 *                          file in either codebase. Flagged rather than silently
 *                          left to look functional.
 *   - 2026-09-15 Claude — This screen now actually reaches the invoice. Its values
 *                          landed in `tax_company_settings`, a table nothing read,
 *                          so an admin could save the company GSTIN and still ship
 *                          tax invoices printing "GST Number: —". Migration 056
 *                          moved the STATUTORY columns onto `site_settings` — the
 *                          issuer of record the documents already read — and the
 *                          endpoint is unchanged, so this screen keeps its
 *                          'Payment Management' module gate.
 *   - 2026-09-16 Claude — Every field on this screen is now bounded, and the four
 *                          statutory codes are SANITISED AS TYPED rather than only
 *                          rejected at Save. Registration No. and HSN / SAC were
 *                          still free text (max-length only), which is how
 *                          "REG-$$$###" and a non-numeric HSN reached a tax
 *                          invoice. Registration No. fixes its character set
 *                          (A-Z, 0-9, hyphen) and leaves length open — a tenant's
 *                          registration instrument is not always a 21-char CIN;
 *                          HSN / SAC fixes both (digits only, 4 / 6 / 8). Every
 *                          rule mirrors taxSettings.routes.js exactly, and each
 *                          field now states its fixed length in a hint so the rule
 *                          is visible before the admin types, not after.
 *   - 2026-09-15 Claude — Company Name / Email / Phone / Address dropped from the
 *                          form. They are the same issuer row now, and Site
 *                          Setting (Site Management) owns them — two screens
 *                          writing one field is how they silently overwrite each
 *                          other. GSTIN and PAN are format-checked and the state
 *                          code must match the GSTIN's first two digits, mirroring
 *                          the server.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import { TAX_SETTINGS_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import { get, put } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
// CIN / registration instrument: character set fixed, length left open (a tenant
// may not hold a 21-char CIN). HSN / SAC: digits only, at the 4 / 6 / 8 lengths
// the GST schedules use.
const REGNO_RE = /^[A-Z0-9-]+$/;
const HSN_RE = /^(\d{4}|\d{6}|\d{8})$/;
const blankOr = (re, msg) => z.union([z.literal(''), z.string().trim().regex(re, msg)]).optional();

// Mirrors admin_backend/src/routes/taxSettings.routes.js. These print on a tax
// invoice, so a typo is not cosmetic: it makes the document unusable for input
// credit and the customer only finds out long after travelling.
const schema = z.object({
  registration_no: blankOr(REGNO_RE, 'Use letters, digits and hyphens only'),
  gst_no: blankOr(GSTIN_RE, 'Enter a valid 15-character GSTIN, e.g. 27AAPCS1234M1ZX'),
  gst_state_code: blankOr(/^[0-9]{2}$/, 'Enter the 2-digit GST state code'),
  pan: blankOr(PAN_RE, 'Enter a valid 10-character PAN, e.g. AAPCS1234M'),
  hsn_code: blankOr(HSN_RE, 'Enter a 4, 6 or 8 digit HSN / SAC code'),
  terms_conditions: z.string().trim().max(50000).optional(),
}).refine(
  (d) => !d.gst_no || !d.gst_state_code || d.gst_no.slice(0, 2) === d.gst_state_code,
  { message: 'GST State Code must match the first two digits of the GSTIN.', path: ['gst_state_code'] },
);

const empty = {
  registration_no: '', gst_no: '', gst_state_code: '', pan: '', hsn_code: '',
  terms_conditions: '',
};

export default function TaxManagement() {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data } = useQuery({ queryKey: qk.taxSettings.get, queryFn: () => get('/tax-settings') });

  useEffect(() => {
    const t = data?.data;
    if (t && Object.keys(t).length) {
      setForm({
        registration_no: t.registration_no || '', gst_no: t.gst_no || '',
        gst_state_code: t.gst_state_code || '', pan: t.pan || '',
        hsn_code: t.hsn_code || '', terms_conditions: t.terms_conditions || '',
      });
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: (body) => put('/tax-settings', body),
    onSuccess: () => {
      toast.ok('Saved', 'Tax settings updated.');
      qc.invalidateQueries({ queryKey: ['tax-settings'] });
      // Same issuer row as Site Setting since migration 056, and it is what
      // useCompany() prints on the tax invoice — without this, an admin saves a
      // GSTIN and the invoice they open next still shows the old one for as long
      // as the 5-minute staleTime holds.
      qc.invalidateQueries({ queryKey: qk.siteSettings.company });
      qc.invalidateQueries({ queryKey: qk.siteSettings.get });
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    mut.mutate(parsed.data);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  // Statutory codes are SANITISED AS TYPED, not merely rejected on submit. The
  // screen these replaced accepted "27AABCU96####3%%%%bb" and "AABCU$$$$$VVVV"
  // into fields that print on a tax invoice; refusing the keystroke is clearer
  // than letting an admin fill the form and discover it at Save. Each `keep`
  // below is exactly the character set its server rule allows, so the client can
  // never hold a value the server would reject on characters alone — only on
  // LENGTH, which is what the inline error states.
  const sanitize = (k, keep, { upper = false } = {}) => (e) => {
    const v = upper ? e.target.value.toUpperCase() : e.target.value;
    setForm({ ...form, [k]: v.replace(keep, '') });
  };
  // GSTIN and PAN: A-Z + 0-9 only, uppercase by definition.
  const onCode = (k) => sanitize(k, /[^A-Z0-9]/g, { upper: true });
  // CIN / registration no.: the same, plus the hyphen real ones carry.
  const onRegNo = (k) => sanitize(k, /[^A-Z0-9-]/g, { upper: true });
  // State code and HSN / SAC are pure numbers.
  const onDigits = (k) => sanitize(k, /[^0-9]/g);

  return (
    <>
      <PageHead title="Tax Management" subtitle="Payment Management · invoice company details" />


      <div className="notice-banner info">{TAX_SETTINGS_NOTE}</div>

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="GST No." err={errors.gst_no}>
            <input value={form.gst_no} onChange={onCode('gst_no')} placeholder="e.g. 27AAPCS1234M1ZX" maxLength={15} />
            <div className="field-hint">Exactly 15 characters: 2-digit state code, 10-char PAN, entity digit, &lsquo;Z&rsquo;, checksum. Printed on every tax invoice as the supplier GSTIN. Leave blank if not GST-registered — the line is then omitted rather than printed empty.</div>
          </Field>
          <Field label="GST State Code" err={errors.gst_state_code}>
            <input value={form.gst_state_code} onChange={onDigits('gst_state_code')} inputMode="numeric" placeholder="e.g. 27" maxLength={2} />
            <div className="field-hint">Exactly 2 digits — the first two digits of the GSTIN above.</div>
          </Field>
          <Field label="PAN" err={errors.pan}>
            <input value={form.pan} onChange={onCode('pan')} placeholder="e.g. AAPCS1234M" maxLength={10} />
            <div className="field-hint">Exactly 10 characters: 5 letters, 4 digits, 1 letter.</div>
          </Field>
          <Field label="Registration No." err={errors.registration_no}>
            <input value={form.registration_no} onChange={onRegNo('registration_no')} placeholder="CIN / company registration no." maxLength={30} />
            <div className="field-hint">Letters, digits and hyphens only.</div>
          </Field>
          <Field label="HSN / SAC Code" err={errors.hsn_code}>
            <input value={form.hsn_code} onChange={onDigits('hsn_code')} inputMode="numeric" placeholder="e.g. 998551" maxLength={8} />
            <div className="field-hint">Digits only, 4, 6 or 8 of them (travel services are SAC 998551).</div>
          </Field>
          <Field label="Terms And Condition" err={errors.terms_conditions} full>
            <textarea rows={6} value={form.terms_conditions} onChange={on('terms_conditions')} placeholder="One clause per line…" />
            <div className="field-hint">One clause per line. These are added <b>after</b> the standard invoice terms, not instead of them.</div>
          </Field>
          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, err, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label}</label>
      {children}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
